
import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, Clock, Star, Bell } from 'lucide-react';

const DrawCalendar = () => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week');

  const upcomingDraws = [
    {
      id: 1,
      game: 'PowerBall',
      date: '2025-01-30',
      time: '20:00',
      jackpot: '$5,000,000',
      status: 'upcoming',
      daysUntil: 2
    },
    {
      id: 2,
      game: 'Oz Lotto',
      date: '2025-01-28',
      time: '20:00',
      jackpot: '$8,000,000',
      status: 'upcoming',
      daysUntil: 0
    },
    {
      id: 3,
      game: 'Saturday Lotto',
      date: '2025-02-01',
      time: '20:00',
      jackpot: '$3,000,000',
      status: 'upcoming',
      daysUntil: 4
    },
    {
      id: 4,
      game: 'PowerBall',
      date: '2025-01-25',
      time: '20:00',
      jackpot: '$2,500,000',
      status: 'completed',
      results: [7, 14, 23, 31, 42, 45, 12]
    },
    {
      id: 5,
      game: 'Oz Lotto',
      date: '2025-01-21',
      time: '20:00',
      jackpot: '$1,800,000',
      status: 'completed',
      results: [3, 18, 26, 33, 39, 44, 8]
    }
  ];

  const weekDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
  const currentWeek = getCurrentWeek();

  function getCurrentWeek() {
    const today = new Date();
    const week = [];
    for (let i = 0; i < 7; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() - today.getDay() + 1 + i); // Start from Monday
      week.push(date);
    }
    return week;
  }

  const getDrawsForDate = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return upcomingDraws.filter(draw => draw.date === dateStr);
  };

  const addReminder = (drawId: number) => {
    // Simulated reminder functionality
    alert('Reminder set! You\'ll be notified 1 hour before the draw.');
  };

  return (
    <div className="space-y-6">
      {/* View Mode Toggle */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Draw Calendar</h2>
        <div className="flex gap-2">
          {['week', 'month'].map((mode) => (
            <Button
              key={mode}
              variant={viewMode === mode ? "default" : "outline"}
              size="sm"
              onClick={() => setViewMode(mode)}
              className={viewMode === mode ? "bg-yellow-600 hover:bg-yellow-700" : ""}
            >
              {mode.charAt(0).toUpperCase() + mode.slice(1)} View
            </Button>
          ))}
        </div>
      </div>

      {/* Week View */}
      {viewMode === 'week' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5 text-yellow-600" />
              This Week's Draws
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-7 gap-2 mb-4">
              {weekDays.map((day, index) => (
                <div key={day} className="text-center p-2 border rounded-lg">
                  <div className="font-medium text-gray-700 mb-1">{day}</div>
                  <div className="text-lg font-bold text-gray-900">
                    {currentWeek[index].getDate()}
                  </div>
                  <div className="mt-2 space-y-1">
                    {getDrawsForDate(currentWeek[index]).map((draw) => (
                      <div
                        key={draw.id}
                        className={`text-xs p-1 rounded ${
                          draw.status === 'upcoming' 
                            ? 'bg-yellow-100 text-yellow-800' 
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {draw.game}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Upcoming Draws List */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="w-5 h-5 text-yellow-600" />
            Upcoming Draws
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {upcomingDraws.filter(draw => draw.status === 'upcoming').map((draw) => (
              <div key={draw.id} className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="text-sm text-gray-600">
                      {new Date(draw.date).toLocaleDateString('en-AU', { weekday: 'short' })}
                    </div>
                    <div className="text-lg font-bold text-gray-900">
                      {new Date(draw.date).getDate()}
                    </div>
                    <div className="text-sm text-gray-600">{draw.time}</div>
                  </div>
                  
                  <div>
                    <h4 className="font-bold text-gray-900 flex items-center gap-2">
                      {draw.game}
                      {draw.daysUntil === 0 && (
                        <Badge className="bg-red-100 text-red-800">Today</Badge>
                      )}
                      {draw.daysUntil === 1 && (
                        <Badge className="bg-orange-100 text-orange-800">Tomorrow</Badge>
                      )}
                    </h4>
                    <p className="text-green-600 font-bold">{draw.jackpot}</p>
                    <p className="text-sm text-gray-600">
                      {draw.daysUntil === 0 ? 'Today' : `${draw.daysUntil} days`}
                    </p>
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addReminder(draw.id)}
                    className="flex items-center gap-1"
                  >
                    <Bell className="w-4 h-4" />
                    Remind Me
                  </Button>
                  <Button
                    size="sm"
                    className="bg-yellow-600 hover:bg-yellow-700"
                  >
                    Play Now
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Recent Results */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Star className="w-5 h-5 text-yellow-600" />
            Recent Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {upcomingDraws.filter(draw => draw.status === 'completed').map((draw) => (
              <div key={draw.id} className="flex items-center justify-between p-4 border rounded-lg">
                <div>
                  <h4 className="font-bold text-gray-900">{draw.game}</h4>
                  <p className="text-sm text-gray-600">{draw.date}</p>
                  <p className="text-green-600 font-bold">{draw.jackpot}</p>
                </div>
                
                <div className="flex flex-wrap gap-1">
                  {draw.results && draw.results.slice(0, -1).map((num, idx) => (
                    <div key={idx} className="w-8 h-8 bg-yellow-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                      {num}
                    </div>
                  ))}
                  {draw.results && draw.results.length > 6 && (
                    <>
                      <span className="text-gray-400 mx-1">+</span>
                      <div className="w-8 h-8 bg-red-600 text-white rounded-full flex items-center justify-center text-sm font-bold">
                        {draw.results[draw.results.length - 1]}
                      </div>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default DrawCalendar;
